// src/components/shop/AddressInput/index.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { Navigation, X, MapPin } from 'lucide-react';

const AddressContainer = styled.div`
  margin-top: 0.5rem;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:focus-within {
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
  }
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  font-size: 0.9rem;
  padding: 0.25rem;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const LocationButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.8;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LocationInfo = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  opacity: 0.7;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .location-icon {
    color: ${props => props.theme?.colors?.accent || '#800000'};
  }
`;

// MOVE functions OUTSIDE component to avoid hoisting issues
const standardizeCoordinates = (coordinates) => {
  if (!coordinates) return null;
  
  // Handle both lat/lng and latitude/longitude formats
  const lat = coordinates.lat ?? coordinates.latitude;
  const lng = coordinates.lng ?? coordinates.longitude;
  
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  return { 
    lat, 
    lng,
    latitude: lat,
    longitude: lng 
  };
};

const formatCoordinates = (coords) => {
  if (!coords) return '';
  const lat = coords.lat ?? coords.latitude;
  const lng = coords.lng ?? coords.longitude;
  if (lat === undefined || lng === undefined) return '';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const AddressInput = ({ address, onAddressChange, onLocationSelect, onMapOpen }) => {
  const [isLoading, setIsLoading] = useState(false);

  // In the handleLiveLocation function, update to:
const handleLiveLocation = async () => {
  if (!navigator.geolocation) {
    console.error('Geolocation not supported');
    return;
  }

  setIsLoading(true);
  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const coords = standardizeCoordinates({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });

      if (coords) {
        const formattedAddress = formatCoordinates(coords);
        
        // Get city from coordinates
        const { getCityFromCoordinates } = await import('../../../firebase/firebaseService');
        const city = await getCityFromCoordinates(coords.lat, coords.lng);
        
        onAddressChange(formattedAddress);
        
        onLocationSelect({
          address: formattedAddress,
          coordinates: coords,
          city: city // Add city data
        });
      }
      setIsLoading(false);
    },
    (error) => {
      console.error('Error getting location:', error);
      alert('Failed to get location. Please enable location services.');
      setIsLoading(false);
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    }
  );
};

  const handleMapOpen = () => {
    console.log('🗺️ Opening map for location selection'); // ADD DEBUG
    if (onMapOpen) {
      onMapOpen();
    }
  };

  return (
    <AddressContainer>
      <InputWrapper>
        <Input
          type="text"
          value={address}
          onChange={(e) => onAddressChange(e.target.value)}
          placeholder="Click map or GPS icon to select location"
          readOnly
        />
        {address && (
          <LocationButton 
            onClick={(e) => { // ADD event parameter
              e.preventDefault(); // ADD
              e.stopPropagation(); // ADD
              onAddressChange('');
              onLocationSelect({ address: '', coordinates: null });
            }}
            title="Clear"
          >
            <X size={16} />
          </LocationButton>
        )}
        <LocationButton 
          onClick={(e) => { // ADD event parameter
            e.preventDefault(); // ADD
            e.stopPropagation(); // ADD
            console.log('📍 Map button clicked'); // ADD DEBUG
            handleMapOpen();
          }}
          title="Select location on map"
        >
          <MapPin size={16} />
        </LocationButton>
      </InputWrapper>
      {address && (
        <LocationInfo>
          <MapPin size={14} className="location-icon" />
          {address}
        </LocationInfo>
      )}
    </AddressContainer>
  );
};

export default AddressInput;